import React, { useState } from 'react';
import { Button } from '@vehix/portal/components/ui/button';
import { Badge } from '@vehix/portal/components/ui/badge';
import { Card, CardContent } from '@vehix/portal/components/ui/card';
import { Switch } from '@vehix/portal/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@vehix/portal/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@vehix/portal/components/ui/dialog';
import {
  MoreVertical,
  Edit,
  Trash2,
  Zap,
  History,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Globe,
  Building2
} from 'lucide-react';
import { useToast } from '@vehix/shared/admin/portal-bridge';
import {
  useAutomationRules,
  useToggleRuleEnabled,
  useDeleteAutomationRule
} from '@vehix/portal/hooks/useScheduling';
import { AutomationRule } from '@vehix/portal/services/api/scheduling';
import { cn } from '@vehix/portal/lib/utils';

interface AutomationRulesListProps {
  tenantId?: string;
  onEdit: (rule: AutomationRule) => void;
  onViewHistory: (rule: AutomationRule) => void;
  onCreateNew: () => void;
}

export const AutomationRulesList: React.FC<AutomationRulesListProps> = ({
  tenantId,
  onEdit,
  onViewHistory,
  onCreateNew
}) => {
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<AutomationRule | null>(null);

  // Fetch rules
  const {
    data: rulesData,
    isLoading,
    error,
    refetch
  } = useAutomationRules({
    tenant_id: tenantId,
    limit: 100
  });

  // Mutations
  const toggleEnabledMutation = useToggleRuleEnabled();
  const deleteMutation = useDeleteAutomationRule();

  const rules = rulesData?.rules || [];

  // Handle toggle enabled
  const handleToggleEnabled = (rule: AutomationRule) => {
    const newEnabled = !rule.is_enabled;

    // Instant feedback
    toast({
      title: newEnabled ? 'Rule Enabled' : 'Rule Disabled',
      description: `${rule.name} has been ${newEnabled ? 'enabled' : 'disabled'}`
    });

    // Background mutation
    toggleEnabledMutation.mutate(
      { id: rule.id, is_enabled: newEnabled },
      {
        onError: error => {
          toast({
            title: 'Failed to update rule',
            description: error instanceof Error ? error.message : 'Unknown error',
            variant: 'destructive'
          });
        }
      }
    );
  };

  // Handle delete
  const handleDelete = () => {
    if (!ruleToDelete) return;

    setDeleteDialogOpen(false);

    // Instant feedback
    toast({
      title: 'Rule Deleted',
      description: `${ruleToDelete.name} has been deleted`
    });

    // Background mutation
    deleteMutation.mutate(ruleToDelete.id, {
      onError: error => {
        toast({
          title: 'Failed to delete rule',
          description: error instanceof Error ? error.message : 'Unknown error',
          variant: 'destructive'
        });
      }
    });

    setRuleToDelete(null);
  };

  // Format trigger conditions for display
  const formatTriggerConditions = (conditions: AutomationRule['trigger_conditions']) => {
    const parts: string[] = [];

    if (conditions.task_name) {
      parts.push(`task "${conditions.task_name}"`);
    } else if (conditions.oem) {
      parts.push(`any ${conditions.oem} data collection`);
    } else {
      parts.push('any data collection');
    }

    if (conditions.min_records) {
      parts.push(`with ${conditions.min_records}+ records`);
    }

    return `When ${parts.join(' ')} completes`;
  };

  // Format action for display
  const formatAction = (rule: AutomationRule) => {
    const config = rule.action_config;
    const parts: string[] = ['Trigger VIN processing'];

    if (config.oem) {
      parts.push(`for ${config.oem}`);
    }

    if (config.sampling_strategy && config.sampling_strategy !== 'all') {
      parts.push(`(${config.sampling_strategy} sample)`);
    }

    return parts.join(' ');
  };

  // Format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className='flex flex-col items-center justify-center py-8 gap-4'>
          <XCircle className='h-8 w-8 text-destructive' />
          <p className='text-muted-foreground'>
            Failed to load automation rules:{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
          <Button variant='outline' onClick={() => refetch()}>
            <RefreshCw className='h-4 w-4 mr-2' />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (rules.length === 0) {
    return (
      <Card>
        <CardContent className='flex flex-col items-center justify-center py-12 gap-4'>
          <Zap className='h-12 w-12 text-muted-foreground' />
          <div className='text-center'>
            <h3 className='text-lg font-semibold'>No Automation Rules Yet</h3>
            <p className='text-muted-foreground mt-1'>
              Create rules to automatically trigger VIN processing after data collection.
            </p>
          </div>
          <Button onClick={onCreateNew}>
            <Zap className='h-4 w-4 mr-2' />
            Create Rule
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className='space-y-4'>
        {/* Header */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <h3 className='text-lg font-medium'>Automation Rules</h3>
            <Badge variant='secondary'>{rules.length}</Badge>
          </div>
          <Button onClick={onCreateNew} size='sm'>
            <Zap className='h-4 w-4 mr-2' />
            Create Rule
          </Button>
        </div>

        {/* Rule Cards */}
        <div className='space-y-3'>
          {rules.map(rule => (
            <Card key={rule.id} className={cn('transition-all', !rule.is_enabled && 'opacity-60')}>
              <CardContent className='p-4'>
                <div className='flex items-start justify-between gap-4'>
                  {/* Left: Rule Info */}
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2'>
                      <div
                        className={cn(
                          'w-2 h-2 rounded-full',
                          rule.is_enabled ? 'bg-green-500' : 'bg-gray-300'
                        )}
                      />
                      <h4 className='font-medium truncate'>{rule.name}</h4>
                      <Badge variant='outline' className='text-xs'>
                        Priority: {rule.priority}
                      </Badge>
                      {rule.tenant_id ? (
                        <Badge variant='secondary' className='text-xs'>
                          <Building2 className='h-3 w-3 mr-1' />
                          Tenant-specific
                        </Badge>
                      ) : (
                        <Badge variant='secondary' className='text-xs'>
                          <Globe className='h-3 w-3 mr-1' />
                          Global
                        </Badge>
                      )}
                    </div>

                    {/* Trigger Conditions */}
                    <p className='text-sm text-muted-foreground mt-2'>
                      <span className='font-medium'>Trigger: </span>
                      {formatTriggerConditions(rule.trigger_conditions)}
                    </p>

                    {/* Action */}
                    <p className='text-sm text-muted-foreground'>
                      <span className='font-medium'>Action: </span>
                      {formatAction(rule)}
                    </p>

                    {/* Execution Stats */}
                    <div className='flex items-center gap-4 mt-3'>
                      <span className='text-xs text-muted-foreground'>
                        Last executed: {formatDate(rule.last_executed_at)}
                      </span>
                      {rule.execution_count > 0 && (
                        <Badge variant='outline' className='text-xs'>
                          <CheckCircle className='h-3 w-3 mr-1' />
                          {rule.execution_count} executions
                        </Badge>
                      )}
                    </div>

                    {rule.description && (
                      <p className='text-sm text-muted-foreground mt-2 line-clamp-2'>
                        {rule.description}
                      </p>
                    )}
                  </div>

                  {/* Right: Actions */}
                  <div className='flex items-center gap-2'>
                    <Switch
                      checked={rule.is_enabled}
                      onCheckedChange={() => handleToggleEnabled(rule)}
                      disabled={toggleEnabledMutation.isPending}
                    />

                    <Button variant='outline' size='sm' onClick={() => onViewHistory(rule)}>
                      <History className='h-4 w-4' />
                      <span className='ml-1 hidden sm:inline'>History</span>
                    </Button>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant='ghost' size='sm'>
                          <MoreVertical className='h-4 w-4' />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='end'>
                        <DropdownMenuItem onClick={() => onEdit(rule)}>
                          <Edit className='h-4 w-4 mr-2' />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onViewHistory(rule)}>
                          <History className='h-4 w-4 mr-2' />
                          View History
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className='text-destructive'
                          onClick={() => {
                            setRuleToDelete(rule);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className='h-4 w-4 mr-2' />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Automation Rule</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{ruleToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant='destructive' onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AutomationRulesList;
